import type {
  AppUsageEntry,
  ClientCommand,
  ClientDto,
  EventCount,
  ForegroundLogEntry,
  LogEvent,
  NoticeSeverity,
  ScreenTimeEntry,
  ScreenTimeSeries,
  SchoolModeCommand,
} from "@/types/protocol";

/**
 * REST-Aufrufe an den KinderService-Server. Fehler kommen als {@link ServerError}
 * zurueck, damit die Oberflaeche eine verstaendliche Meldung zeigen kann.
 */

export class ServerError extends Error {
  constructor(
    message: string,
    readonly status?: number,
    options?: ErrorOptions,
  ) {
    super(message, options);
    this.name = "ServerError";
  }
}

/**
 * Wird gerufen, wenn der Server 401 meldet (Cookie abgelaufen oder abgemeldet).
 * Haengt hier als Callback, damit api/ nichts aus stores/ importieren muss.
 */
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

interface RequestOptions extends RequestInit {
  /** Bei der Anmeldung selbst ist 401 die normale Fehlerantwort, kein Sitzungsende. */
  ignoreUnauthorized?: boolean;
}

async function request<T>(path: string, options?: RequestOptions): Promise<T> {
  const { ignoreUnauthorized, ...init } = options ?? {};

  let response: Response;
  try {
    response = await fetch(path, init);
  } catch (cause) {
    // Technische Ursache nur in der Konsole - in der Oberflaeche steht der Klartext.
    throw new ServerError("Server nicht erreichbar.", undefined, { cause });
  }

  if (response.status === 401 && !ignoreUnauthorized) {
    onUnauthorized?.();
    throw new ServerError("Nicht angemeldet.", 401);
  }

  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new ServerError(errorText(body) || response.statusText, response.status);
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

/**
 * Klartext aus einer Fehlerantwort: `{ error }` der eigenen Endpunkte oder `detail`/`title`
 * eines ProblemDetails - sonst stuende im Toast rohes JSON.
 */
function errorText(body: string): string {
  const text = body.trim();
  if (!text.startsWith("{")) return text;
  try {
    const parsed = JSON.parse(text) as { error?: unknown; detail?: unknown; title?: unknown };
    for (const value of [parsed.error, parsed.detail, parsed.title])
      if (typeof value === "string" && value.trim()) return value.trim();
  } catch {
    // kein JSON - dann eben der Text
  }
  return text;
}

const machinePath = (machine: string) => `/api/clients/${encodeURIComponent(machine)}`;

export const api = {
  /** Version des Servers (ohne Anmeldung abrufbar). */
  version: () => request<{ version: string }>("/api/version"),

  /** Aktuelle Sitzung: angemeldet und als wer? */
  me: () => request<{ authenticated: boolean; user: string | null }>("/api/me"),

  login: (user: string, password: string) =>
    request<{ user: string }>("/api/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user, password }),
      ignoreUnauthorized: true,
    }),

  logout: () => request<{ ok: boolean }>("/api/logout", { method: "POST" }),

  clients: () => request<ClientDto[]>("/api/clients"),

  events: () => request<LogEvent[]>("/api/events"),

  /** Wie oft welche Ereignisart im Zeitraum vorkam. */
  eventSummary: (days = 14) => request<EventCount[]>(`/api/events/summary?days=${days}`),

  screenTime: (date?: string) =>
    request<ScreenTimeEntry[]>(date ? `/api/screentime?date=${encodeURIComponent(date)}` : "/api/screentime"),

  /** Verlauf aller Benutzer für die Auswertung. */
  trend: (days = 14) => request<ScreenTimeSeries[]>(`/api/screentime/history?days=${days}`),

  /** Anwendungen und Kategorien des heutigen Tages. */
  appsToday: () => request<AppUsageEntry[]>("/api/apps/today"),

  /** Wechsel-Historie der Vordergrund-Anwendungen. */
  appLog: (days = 3, limit = 200) => request<ForegroundLogEntry[]>(`/api/apps/log?days=${days}&limit=${limit}`),

  /** Abmelden/Neustart/Herunterfahren - mit optionalem Countdown in Sekunden. */
  command: (machine: string, command: ClientCommand, seconds?: number) =>
    request<{ commandId: string }>(
      `${machinePath(machine)}/${command}${Number.isFinite(seconds) ? `?seconds=${seconds}` : ""}`,
      { method: "POST" },
    ),

  /** Software-Update auf dem PC sofort anstossen. */
  update: (machine: string) => request<{ ok: boolean }>(`${machinePath(machine)}/update`, { method: "POST" }),

  /**
   * Schulmodus eines Benutzers schalten: `on` an, `off` als Ausnahme bis zum Ende des
   * heutigen Planfensters, `auto` zurueck auf den Wochenplan.
   *
   * Der Benutzer steht im Body und nicht in der Route: `ad\jakob` enthaelt einen
   * Backslash, und `%5C` in einem Pfadsegment ist eine bekannte Falle.
   */
  schoolMode: (user: string, mode: SchoolModeCommand, minutes?: number) =>
    request<{ user: string; active: boolean; until: string | null; source: string }>("/api/schoolmode", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user, mode, minutes }),
    }),

  /** Textnachricht im Popup-Fenster der Tray-App anzeigen. */
  notify: (machine: string, text: string, severity: NoticeSeverity = "info") =>
    request<{ ok: boolean }>(`${machinePath(machine)}/notify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text, severity }),
    }),
};
